import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { PreviewType } from '../../types';
import { PreviewRenderer } from '../previews/PreviewRenderer';

interface ActionConfirmationCardProps {
  toolName: string;
  description: string;
  previewType?: PreviewType;
  previewData?: any;
  onConfirm: () => void;
  onCancel: () => void;
  status: 'pending' | 'confirming' | 'confirmed' | 'cancelled' | 'error';
  errorMessage?: string;
}

export function ActionConfirmationCard({
  toolName,
  description,
  previewType,
  previewData,
  onConfirm,
  onCancel,
  status,
  errorMessage,
}: ActionConfirmationCardProps) {
  const isPending = status === 'pending';
  const isLoading = status === 'confirming';

  return (
    <Card className="border-amber-400 border-2">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <span className="text-amber-600">⚠</span>
          Action: {toolName}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardHeader>

      <CardContent className="p-4 pt-0">
        {previewType && previewData && (
          <div className="mt-2">
            <PreviewRenderer previewType={previewType} data={previewData} />
          </div>
        )}

        {status === 'confirmed' && (
          <p className="text-sm text-green-600 font-medium mt-2">✓ Confirmed</p>
        )}
        {status === 'cancelled' && (
          <p className="text-sm text-muted-foreground mt-2">Cancelled</p>
        )}
        {status === 'error' && (
          <p className="text-sm text-red-600 mt-2">{errorMessage ?? 'An error occurred'}</p>
        )}
      </CardContent>

      {(isPending || isLoading) && (
        <CardFooter className="p-4 pt-0 gap-2">
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {isLoading ? 'Confirming…' : 'Confirm'}
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
